import firebase from "firebase"
import { db, auth } from "../../../utils/firebase"

import { AI_TOOLING_COLLECTION, getToolById } from "../registry"

/**
 * Pull a tool's saved config from Firestore. Returns `null` when the document
 * doesn't exist yet — callers seed from `tool.defaults` in that case. The
 * page treats "doc missing" and "saved doc" as the same surface; the only
 * difference is whether the Save button is creating vs. updating.
 */
export const loadToolConfig = async (toolId) => {
	const snap = await db.collection(AI_TOOLING_COLLECTION).doc(toolId).get()
	if (!snap.exists) return null
	return snap.data() || null
}

/**
 * Write a tool's edited config back to Firestore. Stamps the updater + a
 * server-side timestamp so the admin page can show "last saved by X at Y"
 * without having to trust client clocks. Schema is serialised as a JSON
 * string so deep nested objects survive Firestore's "no fields named with a
 * leading digit / dot" restrictions on map keys.
 */
export const saveToolConfig = async (toolId, config) => {
	const tool = getToolById(toolId)
	if (!tool) throw new Error(`Unknown AI tool: ${toolId}`)

	await db
		.collection(AI_TOOLING_COLLECTION)
		.doc(toolId)
		.set(
			{
				tool_id: toolId,
				model: config.model,
				temperature: config.temperature,
				system_instructions: config.system_instructions,
				user_instructions: config.user_instructions,
				json_schema: config.json_schema,
				json_schema_name: config.json_schema_name,
				json_schema_strict: config.json_schema_strict,
				response_format: "json_schema",
				updated_at: firebase.firestore.FieldValue.serverTimestamp(),
				updated_by: auth.currentUser?.uid || null,
			},
			{ merge: true }
		)
}

/**
 * Best-effort JSON parse helper used by the textarea editor. Returns
 * `{ ok: true, value }` on success and `{ ok: false, error }` otherwise so
 * the form can surface a parse error inline without throwing.
 */
export const parseJsonSafe = (raw) => {
	try {
		return { ok: true, value: JSON.parse(raw) }
	} catch (err) {
		return { ok: false, error: err?.message || String(err) }
	}
}
